import { Location } from '@angular/common';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { serverTimestamp, Timestamp } from '@angular/fire/firestore';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { intersects, select, sift } from 'radash';
import {
  catchError,
  EMPTY,
  map,
  mergeMap,
  Observable,
  of,
  Subject,
  switchMap,
  take,
  takeUntil,
  withLatestFrom,
} from 'rxjs';
import { UserModel } from '../..//models/user.model';
import { GroupType } from '../../models/group.model';
import { SchoolMemberType } from '../../models/member.school.model';
import { MembershipStatus } from '../../models/membership.status';
import { Notice } from '../../models/notice.model';
import { NoticeService } from '../../services/notice.service';
import * as groupActions from '../actions/group.actions';
import * as noticeActions from '../actions/notice.actions';
import * as teacherActions from '../actions/teacher.school.actions';
import * as userActions from '../actions/user.actions';
import { AppState } from '../appstate';
import * as groupSelector from '../selectors/group.selector';
import * as noticeSelector from '../selectors/notice.selector';

@Injectable()
export class NoticeEffects implements OnDestroy {
  actions$ = inject(Actions);
  store = inject(Store<AppState>);
  location = inject(Location);
  noticeService: NoticeService = inject(NoticeService);
  toastr: ToastrService = inject(ToastrService);
  afs: AngularFirestore = inject(AngularFirestore);
  router: Router = inject(Router);

  user: UserModel | null = null;

  private logout$ = new Subject<void>();

  constructor() {
    this.store
      .select((state) => state.user.user)
      .pipe(takeUntil(this.logout$))
      .subscribe((user) => {
        this.user = user;
      });
  }

  ngOnDestroy(): void {
    this.logout$.next();
    this.logout$.complete();
  }

  // Effect to fetch group Notices
  getNotices$ = createEffect(() =>
    this.actions$.pipe(
      ofType(noticeActions.getNotices),
      withLatestFrom(this.store.select(groupSelector.getSelectedGroup)),
      switchMap(
        ([
          { groupId, memberType, academicYear, gradeId, startDate, lastRecord },
          group,
        ]) => {
          console.log('NoticeEffects :: getNotices$ triggered', {
            groupId,
            memberType,
            academicYear,
            gradeId,
            user: this.user ? { uid: this.user.uid, email: this.user.email } : null,
            group: group ? { id: group.id, groupType: group.groupType, memberStatus: group.memberStatus } : null
          });
          
          if (group?.groupType !== GroupType.school && group?.groupType !== GroupType.church) {
            console.warn('NoticeEffects :: getNotices$ aborted: groupType is neither school nor church:', group?.groupType);
            return of(noticeActions.getNoticesFailure({ error: { code: 'INVALID_GROUP_TYPE', message: 'Group type must be school or church', title: 'Error Fetching Notices' } }));
          }
          if (this.user == null) {
            console.warn('NoticeEffects :: getNotices$ aborted: this.user is null');
            return of(noticeActions.getNoticesFailure({ error: { code: 'USER_UNAUTHENTICATED', message: 'User is not logged in', title: 'Error Fetching Notices' } }));
          }
          if (group?.memberStatus !== MembershipStatus.active) {
            console.warn('NoticeEffects :: getNotices$ aborted: memberStatus is not active:', group?.memberStatus);
            return of(noticeActions.getNoticesFailure({ error: { code: 'MEMBERSHIP_INACTIVE', message: 'Membership is not active', title: 'Error Fetching Notices' } }));
          }

          this.logout$.next();
          console.log('NoticeEffects :: getNotices$ - calling getNoticesByGroup...');
          return this.noticeService.getNoticesByGroup({
            groupId: groupId,
            memberType,
            academicYear,
            startDate,
            limitRecords: 10,
            gradeIds: gradeId,
            lastRecord: lastRecord,
          }).pipe(
            map((notices) => {
              console.log('NoticeEffects :: getNotices$ success, notices count:', notices?.length);
              return noticeActions.addNoticesToStore({ notices });
            }),
            catchError((error) => {
              console.error('NoticeEffects :: getNotices$ - service call error:', error);
              return of(noticeActions.getNoticesFailure({ error }));
            })
          );
        }
      ),
      catchError((error) => {
        console.error('NoticeEffects :: getNotices$ - caught error in outer block:', error);
        this.toastr.error(
          'An error occurred. Please try again later.',
          'Error Fetching Notices'
        );
        return of(noticeActions.getNoticesFailure({ error }));
      })
    )
  );

  getChildGradeNotices$ = createEffect(() =>
    this.actions$.pipe(
      ofType(teacherActions.getChildrenDone),
      withLatestFrom(this.store.select(groupSelector.getSelectedGroup)),
      switchMap(([{ members }, group]) => {
        // console.log(`getNotices$: ${groupId}`);
        this.logout$.next();
        if (members?.length === 0) return EMPTY;
        const gradeIds = sift(
          select(
            members,
            (m) => m.gradeId,
            (m) => m.status === MembershipStatus.active
          )
        );

        return this.noticeService.getNoticesByGroup({
          groupId: group?.id!,
          memberType: group?.memberType!,
          academicYear: group?.academicYear,
          startDate: undefined,
          limitRecords: 10,
          gradeIds: gradeIds.length > 0 ? gradeIds : undefined,
        });
      }),
      switchMap((notices) => {
        return of(
          noticeActions.addNoticesToStore({
            notices: notices,
          })
        );
      }),
      catchError((error) => {
        this.toastr.error(
          'An error occurred. Please try again later.',
          'Error Fetching Notices'
        );
        return of(noticeActions.getNoticesFailure({ error }));
      })
    )
  );

  loadNotices$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(groupActions.select),
      withLatestFrom(this.store.select(groupSelector.getSelectedGroup)),
      switchMap(([{ groupId }, group]) => {
        if (group?.groupType !== GroupType.school && group?.groupType !== GroupType.church) return EMPTY;
        if (
          group?.groupType === GroupType.school &&
          intersects(group?.memberType as SchoolMemberType[], [
            SchoolMemberType.unassigned,
          ])
        ) {
          return EMPTY;
        }
        return this.store.select(noticeSelector.getNoticesBySelectedGroup);
      }),
      switchMap((notices) => {
        if (notices == undefined || notices?.length > 0) return EMPTY;
        return this.store.select(groupSelector.getSelectedGroup).pipe(
          take(1),
          switchMap((group) => {
            if (group == undefined) return EMPTY;
            return of(
              noticeActions.getNotices({
                groupId: group.id,
                memberType: group.memberType as any[],
                academicYear: group.academicYear,
                gradeId: group.groupType === GroupType.school &&
                  intersects(group?.memberType as SchoolMemberType[], [
                    SchoolMemberType.president,
                    SchoolMemberType.principal,
                  ])
                    ? undefined
                    : group.gradeId
                    ? [group.gradeId]
                    : undefined,
              })
            );
          })
        );
      }),
      catchError((error) => {
        return of(noticeActions.getNoticesFailure({ error }));
      })
    )
  );

  // Fetch recently created Notice
  getNotice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(noticeActions.createSuccess),
      switchMap(({ notice }) => {
        return this.afs
          .collection('groups')
          .doc(notice.groupId)
          .collection<Notice>('notices')
          .doc(notice.id)
          .get();
      }),
      switchMap((snapshot) => {
        const notice = snapshot.data() as Notice;
        notice.id = snapshot.id;
        return of(
          noticeActions.addNoticeToStore({
            notice: notice,
          })
        );
      })
    )
  );

  // Create Notice
  create$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(noticeActions.create),
      mergeMap(async ({ id, notice }) => {
        if (this.user == null) {
          this.toastr.error(
            'User not found. Please try again later',
            'Error Creating Notice'
          );
          return noticeActions.createFailure({ error: 'User not found' });
        }

        const now = Timestamp.now();
        const e: any = {
          ...notice,
          id: id,
          groupId: notice.groupId,
          createdBy: this.user?.name,
          createdById: this.user?.uid,
          created: now,
          modified: now,
        };
        try {
          await this.noticeService.create(id, {
            ...e,
            created: serverTimestamp(),
            modified: serverTimestamp(),
          });
          this.toastr.success(
            'The Notice has been created successfully.',
            'Notice Created Successfully'
          );
          this.store.dispatch(
            noticeActions.addNoticeToStore({ notice: e as Notice })
          );
          this.location.back();
          return noticeActions.createSuccess({ notice: e as Notice });
        } catch (error) {
          console.error(`create$: ${error}`);
          this.toastr.error(
            `An error occurred, '${error}'. Please try again later.`,
            'Error Creation Failed'
          );
          return noticeActions.createFailure({ error });
        }
      })
    )
  );

  // Update Notice
  update$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(noticeActions.update),
      switchMap(async ({ groupId, id, notice }) => {
        if (this.user == null) {
          this.toastr.error(
            'User not found. Please try again later',
            'Error Updating Notice'
          );
          return noticeActions.updateFailure({ error: 'User not found' });
        }

        const now = Timestamp.now();
        const e: Notice = {
          ...notice,
          id: id,
          groupId: groupId,
          updatedBy: this.user!.name!,
          updatedById: this.user!.uid!,
          modified: now,
        };

        try {
          await this.noticeService.update(groupId, id, {
            ...e,
            modified: serverTimestamp() as Timestamp,
          });
          this.toastr.success(
            'The Notice has been updated successfully.',
            'Notice Updated Successfully'
          );
          this.store.dispatch(
            noticeActions.addNoticeToStore({ notice: { ...e, id: id } as Notice })
          );
          this.location.back();
          return noticeActions.updateSuccess({
            notice: { ...e, id: id } as Notice,
          });
        } catch (error: any) {
          console.error(`update$: ${error}`);
          this.toastr.error(
            `An error occurred, '${error}'. Please try again later.`,
            'Error Updating Notice'
          );
          return noticeActions.updateFailure({ error });
        }
      })
    )
  );

  // effect to delete a notice
  delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(noticeActions.deleteAction),
      switchMap(({ groupId, id }) =>
        this.noticeService
          .delete(groupId, id)
          .then(() => {
            this.toastr.success(
              'The notice has been deleted successfully.',
              'Notice Deleted'
            );
            return noticeActions.deleteSuccess({ id: id });
          })
          .catch((error: any) => {
            // console.error(`Error deleting notice: ${error}`);
            this.toastr.error(
              `An error occurred, '${error}'. Please try again later.`,
              'Error Deleting Notice'
            );
            return noticeActions.deleteFailure({ error });
          })
      )
    )
  );

  // Get Notice Statistics
  //   getNoticeStatistics$: Observable<Action> = createEffect(() =>
  //     this.actions$.pipe(
  //       ofType(
  //         noticeActions.createSuccess,
  //         noticeActions.updateStatusSuccess
  //       ),
  //       switchMap((action) => {
  //         let groupId = '';
  //         if (action.type === noticeActions.createSuccess.type) {
  //           groupId = action.notice.groupId;
  //         } else if (action.type === noticeActions.updateStatusSuccess.type) {
  //           groupId = action.notice.groupId;
  //         }

  //         return from(this.noticeService.getNoticeStats(groupId)).pipe(
  //           switchMap((snapshot) => {
  //             let g: any = {
  //               id: groupId,
  //               activeNotices: snapshot?.upcoming ?? '-',
  //               completedNotices: snapshot?.completed ?? '-',
  //             };
  //             return of(noticeActions.getNoticeStatsDone({ group: g }));
  //           }),
  //           catchError((error) =>
  //             of(noticeActions.getNoticeStatsFailure({ error }))
  //           )
  //         );
  //       })
  //     )
  //   );

  clearNotices$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.logoutSuccess),
      switchMap(() => {
        this.logout$.next();
        return of(noticeActions.clear());
      })
    )
  );
}
